<template>
    <div>
        <!-- ══ LIST ══════════════════════════════════════════════════════════ -->
        <div v-if="!selected" class="card">
            <div class="card-header">
                <div>
                    <h4 class="card-header-title">MCP servers</h4>
                    <p class="settings-subtitle mb-0">
                        Where PAICE gets its tools — Aqfer's own servers, and
                        anywhere else you connect.
                    </p>
                </div>
                <button class="btn btn-primary btn-sm" @click="openAdd" v-if="externalServers.length">
                    <i class="fa fa-plus me-1"></i>Add server
                </button>
            </div>

            <!-- A tally of what PAICE will actually do, in the same colours the
                 rails use — so the list has an answer before it is read. -->
            <div v-if="!loading && !loadError && servers.length" class="mcp-tally">
                <span v-for="entry in tally" :key="entry.status" class="mcp-tally-item">
                    <span class="mcp-dot" :class="`mcp-dot-${entry.status}`"></span>
                    <strong>{{ entry.count }}</strong> {{ entry.label }}
                </span>
            </div>

            <div class="card-body">
                <div v-if="loading" class="settings-loading">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading servers…</span>
                </div>

                <div v-else-if="loadError" class="text-center card bg-light mb-0 p-3 border border-dashed">
                    <p class="text-muted mb-0">
                        <i class="fa-solid fa-circle-exclamation me-1"></i>
                        {{ loadError }}
                    </p>
                </div>

                <template v-else>
                    <!-- Aqfer's own servers first, then everything connected from
                         outside: the two answer different questions, and which one
                         a row belongs to changes what it means to switch it on. -->
                    <div v-for="group in serverGroups" :key="group.key" class="mcp-group">
                        <p class="mcp-eyebrow">{{ group.title }}</p>
                        <ul class="mcp-list">
                            <li v-for="server in group.servers" :key="server.serverId">
                                <button type="button" class="mcp-row" @click="open(server)">
                                    <span class="mcp-rail" :class="`mcp-rail-${statusOf(server)}`" aria-hidden="true"></span>
                                    <span class="mcp-icon" :class="{ 'mcp-icon-off': statusOf(server) === 'off' }">
                                        <i class="fa-solid fa-plug"></i>
                                    </span>
                                    <span class="mcp-body">
                                        <span class="mcp-title-row">
                                            <span class="mcp-name">{{ server.name }}</span>
                                            <span v-if="server.builtin" class="mcp-tag mcp-tag-builtin">
                                                Built-in
                                            </span>
                                            <span v-else-if="server.scope === 'shared'" class="mcp-tag mcp-tag-shared">
                                                Shared
                                            </span>
                                            <span v-else class="mcp-tag mcp-tag-private">Private</span>
                                        </span>
                                        <span class="mcp-meta">
                                            <code class="mcp-mono">{{ hostOf(server.url) }}</code>
                                            <span class="mcp-sep">·</span>
                                            {{ authLabel(server) }}
                                        </span>
                                    </span>
                                    <span class="mcp-status" :class="`mcp-status-${statusOf(server)}`">
                                        {{ statusLabel(server) }}
                                    </span>
                                    <i class="fa fa-chevron-right mcp-chevron" aria-hidden="true"></i>
                                </button>
                            </li>
                        </ul>
                    </div>

                    <!-- The eyebrow stays a left-aligned section heading like the
                         groups above it; only the prompt inside the box is centred. -->
                    <div v-if="!externalServers.length" class="mcp-group">
                        <p class="mcp-eyebrow">External</p>
                        <div class="mcp-empty mcp-empty-boxed">
                            <span class="mcp-empty-icon"><i class="fa-solid fa-plug"></i></span>
                            <p class="mcp-empty-title mb-1">No other servers yet</p>
                            <p class="settings-help mb-3">
                                Aqfer's own servers are above. Add an MCP server to give
                                PAICE tools from somewhere else — your issue tracker, your
                                warehouse, your own internal service.
                            </p>
                            <button class="btn btn-primary btn-sm" @click="openAdd">
                                <i class="fa fa-plus me-1"></i>Add server
                            </button>
                        </div>
                    </div>

                    <p v-else class="mcp-footnote">
                        <i class="fa-solid fa-circle-info me-1"></i>
                        Switching a server on adds its tools to every PAICE chat on
                        {{ cid }} — this is a client setting, not a personal one.
                    </p>
                </template>
            </div>
        </div>

        <!-- ══ DETAIL ════════════════════════════════════════════════════════ -->
        <template v-else>
            <button type="button" class="mcp-back" @click="close">
                <i class="fa fa-arrow-left me-1 pe-1"></i>MCP servers
            </button>

            <div class="card">
                <div class="card-header">
                    <div>
                        <h4 class="card-header-title">
                            {{ selected.name }}
                            <span v-if="selected.builtin" class="mcp-tag mcp-tag-builtin ms-1">Built-in</span>
                            <span v-else-if="selected.scope === 'shared'" class="mcp-tag mcp-tag-shared ms-1">Shared</span>
                            <span v-else class="mcp-tag mcp-tag-private ms-1">Private</span>
                        </h4>
                        <p class="settings-subtitle mb-0">
                            {{ selected.description || 'No description.' }}
                        </p>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <button class="btn btn-outline-secondary btn-sm" :disabled="testing" @click="test">
                            <span v-if="testing" class="spinner-border spinner-border-sm me-1" role="status" style="width: 1em; height: 1em;">
                                <span class="visually-hidden">Testing</span>
                            </span>
                            Test connection
                        </button>
                        <button v-if="canModify(selected)" class="btn btn-outline-primary btn-sm" @click="openEdit">
                            Edit
                        </button>
                        <button v-if="canModify(selected)" class="btn btn-outline-danger btn-sm" @click="confirmDelete">
                            Delete
                        </button>
                    </div>
                </div>

                <div class="card-body">
                    <!-- What happens the next time PAICE reaches for this server,
                         stated once, at the top. The rail on the list row is the
                         same claim in colour; this is it in words. -->
                    <div class="mcp-verdict" :class="`mcp-verdict-${verdict.status}`">
                        <span class="mcp-verdict-rail" aria-hidden="true"></span>
                        <i class="fa-solid" :class="verdict.icon" aria-hidden="true"></i>
                        <span class="mcp-verdict-text">
                            <span class="mcp-verdict-title">{{ verdict.title }}</span>
                            <span class="mcp-verdict-detail">{{ verdict.detail }}</span>
                        </span>
                    </div>

                    <p class="mcp-eyebrow">Configuration</p>
                    <dl class="mcp-facts">
                        <div>
                            <dt>Endpoint</dt>
                            <dd>
                                <code v-if="selected.url" class="mcp-mono">{{ selected.url }}</code>
                                <span v-else class="text-muted">Not deployed for {{ cid }}</span>
                            </dd>
                        </div>
                        <div>
                            <dt>Tool names</dt>
                            <dd>
                                <template v-if="selected.toolPrefix">
                                    prefixed <code class="mcp-mono">{{ selected.toolPrefix }}_</code>
                                </template>
                                <template v-else>as the server names them</template>
                            </dd>
                        </div>
                        <div>
                            <dt>Authentication</dt>
                            <dd>{{ authLabel(selected) }}</dd>
                        </div>
                        <div>
                            <dt>Trust</dt>
                            <dd>{{ selected.trustLevel === 'first_party' ? 'Aqfer-operated' : 'Third party' }}</dd>
                        </div>
                        <div v-if="selected.updatedAt">
                            <dt>Last changed</dt>
                            <dd>
                                {{ DateTimeFormat(selected.updatedAt) }}
                                <template v-if="selected.createdBy"> by {{ selected.createdBy }}</template>
                            </dd>
                        </div>
                    </dl>
                    <p class="mcp-eyebrow mt-4">Availability</p>
                    <div>
                        <div class="mcp-toggle">
                            <div class="form-check form-switch mb-0">
                                <input :id="`mcp-enabled-${selected.serverId}`" class="form-check-input" type="checkbox"
                                    role="switch" :checked="isEnabled(selected)"
                                    :disabled="togglingEnabled || !canToggleEnabled"
                                    @change="toggleEnabled" />
                                <label class="form-check-label" :for="`mcp-enabled-${selected.serverId}`">
                                    Available to PAICE
                                </label>
                            </div>
                            <!-- Said plainly, because the blast radius is the whole
                                 client: there is no personal copy of this switch to
                                 fall back on if it is flipped by mistake. -->
                            <p class="settings-help mb-0">
                                <template v-if="isPrivate(selected)">
                                    Only you can see this server, so this switch only
                                    affects your own chats.
                                </template>
                                <template v-else>
                                    This applies to everyone on {{ cid }} — disabling it
                                    {{ isEnabled(selected) ? 'removes' : 'on adds' }}
                                    these tools for every PAICE user in this client.
                                </template>
                                <template v-if="selected.builtin">
                                    Built in to PAICE — its tools are on unless they are
                                    turned off here.
                                </template>
                            </p>
                            <p v-if="!canToggleEnabled" class="mcp-help mb-0">
                                <i class="fa fa-lock me-1"></i>
                                Read only — only admins can change what PAICE uses for
                                {{ cid }}.
                            </p>
                        </div>
                    </div>

                    <!-- The reason to press Test: proof, in the server's own
                         vocabulary, that the thing on the other end is real. -->
                    <div v-if="testResult" class="mcp-test mt-3" :class="testResult.ok ? 'mcp-test-ok' : 'mcp-test-bad'">
                        <i class="fa-solid mt-1" :class="testResult.ok ? 'fa-circle-check' : 'fa-circle-exclamation'"></i>
                        <div>
                            <template v-if="testResult.ok">
                                <p class="mb-0">
                                    Answered in {{ testResult.elapsedMs }} ms with
                                    {{ testResult.toolCount }}
                                    {{ testResult.toolCount === 1 ? 'tool' : 'tools' }}.
                                </p>
                                <p v-if="testResult.tools && testResult.tools.length" class="mcp-test-tools mb-0">
                                    {{ testResult.tools.slice(0, 8).join(', ')
                                    }}<template v-if="testResult.tools.length > 8">, …</template>
                                </p>
                            </template>
                            <p v-else class="mb-0">{{ testResult.error }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Credential: sign-in (OAuth) ──────────────────────────────── -->
            <!--
              Separate card from the API-key one rather than a branch inside it:
              the two share no field. One takes a secret the user pastes, the
              other takes a round trip through a third party's consent screen and
              stores nothing the user ever sees.
            -->
            <div v-if="needsCredential(selected) && isOAuth(selected)" class="card">
                <div class="card-header">
                    <div>
                        <h4 class="card-header-title">Your connection</h4>
                        <p class="settings-subtitle mb-0">
                            You sign in to {{ hostOf(selected.url) || 'the server' }} and
                            grant PAICE access. Everyone on {{ cid }} connects their own
                            account — an OAuth sign-in cannot be shared.
                        </p>
                    </div>
                    <span v-if="oauthConnected" class="mcp-tag mcp-tag-ready">Connected</span>
                    <span v-else-if="oauthNeedsReauth" class="mcp-tag mcp-tag-attention">Expired</span>
                </div>

                <div class="card-body">
                    <div v-if="credentialLoading" class="settings-loading">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <span>Checking…</span>
                    </div>

                    <template v-else>
                        <div v-if="oauthNeedsReauth" class="mcp-note mcp-note-error">
                            <i class="fa-solid fa-circle-exclamation"></i>
                            <span>
                                The server no longer accepts the stored authorization. Connect
                                again to restore it.
                            </span>
                        </div>
                        <!--
                          Past the access token's expiry but able to renew itself.
                          Called out rather than hidden so a stale-looking
                          "expires at" in the details below does not read as a
                          fault — but styled as success, because it is one.
                        -->
                        <div v-else-if="oauthRenewsAutomatically" class="mcp-note mcp-note-success">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>
                                Connected. The current access token has run out, which is
                                normal between conversations — PAICE renews it on your next
                                message. Nothing for you to do.
                            </span>
                        </div>
                        <div v-else-if="oauthConnected" class="mcp-note mcp-note-success">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>
                                Connected. PAICE refreshes the access token on its own. You
                                will only be asked again if you revoke it at the server.
                            </span>
                        </div>
                        <div v-else class="mcp-note mcp-note-warning">
                            <i class="fa-solid fa-triangle-exclamation"></i>
                            <span>
                                Not connected yet. PAICE skips this server's tools until you
                                sign in.
                            </span>
                        </div>

                        <p v-if="oauthScopes.length" class="mcp-help mb-0">
                            Granted: <span class="mcp-mono">{{ oauthScopes.join(', ') }}</span>
                        </p>
                    </template>
                </div>

                <div class="card-footer" v-if="!credentialLoading">
                    <div class="footer-status">
                        <span v-if="credentialError" class="text-danger">
                            <i class="fa-solid fa-circle-exclamation me-1"></i>{{ credentialError }}
                        </span>
                        <span v-else-if="connectingOAuth" class="text-muted">
                            Waiting for the sign-in window…
                        </span>
                        <span v-else-if="oauthRenewsAutomatically" class="text-muted">
                            Renews on your next message
                        </span>
                        <span v-else-if="oauthConnected" class="text-muted">
                            Signed in as you — not shared with {{ cid }}
                        </span>
                        <span v-else class="text-muted">Not connected</span>
                    </div>
                    <div class="d-flex gap-2">
                        <button v-if="oauthConnected || oauthNeedsReauth" class="btn btn-outline-danger btn-sm"
                            :disabled="connectingOAuth || savingCredential" @click="confirmRevokeCredential">
                            Disconnect
                        </button>
                        <button class="btn btn-primary btn-sm" :disabled="connectingOAuth"
                            @click="connectOAuth(selected)">
                            <span v-if="connectingOAuth" class="spinner-border spinner-border-sm me-1" role="status"
                                style="width:1em; height:1em">
                                <span class="visually-hidden">Connecting</span>
                            </span>
                            {{ oauthConnected || oauthNeedsReauth ? 'Reconnect' : 'Connect' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── Credential: API key ──────────────────────────────────────── -->
            <div v-if="needsCredential(selected) && !isOAuth(selected)" class="card">
                <div class="card-header">
                    <div>
                        <h4 class="card-header-title">API key</h4>
                        <p class="settings-subtitle mb-0">
                            <template v-if="selected.credentialOwnership === 'shared'">
                                One key for the whole of {{ cid }}.
                            </template>
                            <template v-else>
                                Your own key. Other people on {{ cid }} save their own.
                            </template>
                        </p>
                    </div>
                    <span v-if="credentialSaved" class="mcp-tag mcp-tag-ready">Saved</span>
                </div>

                <div class="card-body">
                    <div v-if="credentialLoading" class="settings-loading">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <span>Checking…</span>
                    </div>

                    <template v-else>
                        <!-- Same tinted note as the OAuth card above: both cards
                             answer "is this credential in place?", so both should
                             answer it in the same shape. -->
                        <div v-if="credentialSaved" class="mcp-note mcp-note-success">
                            <i class="fa-solid fa-circle-check"></i>
                            <span>
                                A key is saved and encrypted. It is never shown again —
                                paste a new one to replace it.
                            </span>
                        </div>
                        <div v-else class="mcp-note mcp-note-warning">
                            <i class="fa-solid fa-triangle-exclamation"></i>
                            <span>
                                No key saved yet. PAICE skips this server's tools until
                                there is one.
                            </span>
                        </div>

                        <div class="mcp-field mb-0">
                            <label class="form-label" :for="`mcp-key-${selected.serverId}`">
                                {{ credentialSaved ? 'Replace key' : 'API key' }}
                            </label>
                            <div class="input-group">
                                <input :id="`mcp-key-${selected.serverId}`" :value="apiKeyDraft"
                                    :type="apiKeyRevealed ? 'text' : 'password'"
                                    class="form-control mcp-mono" autocomplete="off" spellcheck="false"
                                    autocapitalize="off" autocorrect="off"
                                    :disabled="!canSetCredential || savingCredential"
                                    placeholder="Paste the key from the server's own settings"
                                    @input="onApiKeyInput" @keyup.enter="submitKey" />
                                <!-- A masked paste is unverifiable, and the key is
                                     never shown again once saved — so the one moment
                                     it can be checked is before pressing Save. -->
                                <button class="btn btn-light" type="button"
                                    :disabled="!apiKeyDraft || savingCredential"
                                    :aria-pressed="apiKeyRevealed"
                                    :aria-label="apiKeyRevealed ? 'Hide the key' : 'Show the key'"
                                    @click="apiKeyRevealed = !apiKeyRevealed">
                                    <i class="fa-solid" :class="apiKeyRevealed ? 'fa-eye' : 'fa-eye-slash'"></i>
                                </button>
                            </div>
                            <p v-if="apiKeyDraft" class="mcp-help mb-0">
                                {{ apiKeyDraft.length }} characters. Press Enter to save.
                            </p>
                        </div>

                    </template>
                </div>

                <div class="card-footer" v-if="!credentialLoading">
                    <div class="footer-status">
                        <span v-if="credentialError" class="text-danger">
                            <i class="fa-solid fa-circle-exclamation me-1"></i>{{ credentialError }}
                        </span>
                        <span v-else-if="!canSetCredential" class="text-muted">
                            <i class="fa fa-lock me-1"></i>
                            Read only — only admins can set the shared key.
                        </span>
                        <span v-else-if="apiKeyDraft.trim()" class="text-muted">Not saved yet</span>
                        <span v-else-if="credentialSaved" class="text-muted">Key saved and encrypted</span>
                        <span v-else class="text-muted">No key saved</span>
                    </div>
                    <div class="d-flex gap-2">
                        <button v-if="credentialSaved && canSetCredential" class="btn btn-outline-danger btn-sm"
                            :disabled="savingCredential" @click="confirmRevokeCredential">
                            Remove key
                        </button>
                        <button class="btn btn-primary btn-sm"
                            :disabled="!apiKeyDraft.trim() || !canSetCredential || savingCredential"
                            @click="saveCredential">
                            <span v-if="savingCredential" class="spinner-border spinner-border-sm me-1" role="status"
                                style="width:1em; height:1em">
                                <span class="visually-hidden">Saving</span>
                            </span>
                            Save key
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── Tool permissions ─────────────────────────────────────────
                 One decision per tool, for the whole client, and the only place
                 permissions are set — nothing a user clicks in a chat changes
                 what is here. The card says so at the top, because "ask" is the
                 setting people expect to be able to dismiss and cannot. -->
            <div class="card">
                <div class="card-header">
                    <div>
                        <h4 class="card-header-title">Tool permissions</h4>
                        <p class="settings-subtitle mb-0">
                            What PAICE may do with this server's tools, for everyone
                            on {{ cid }}. A tool set to Ask asks every time it is used.
                        </p>
                    </div>
                    <button class="btn btn-outline-secondary btn-sm" :disabled="toolsLoading"
                        @click="loadTools({ refresh: true })">
                        <span v-if="toolsLoading" class="spinner-border spinner-border-sm me-1" role="status"
                            style="width: 1em; height: 1em;">
                            <span class="visually-hidden">Loading</span>
                        </span>
                        Refresh tools
                    </button>
                </div>

                <div class="card-body">
                    <div v-if="toolsLoading && !tools.length" class="settings-loading">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <span>Reading the server's tools…</span>
                    </div>

                    <template v-else>
                        <!-- The catalog and the policy have separate fates: the
                             policy is in force whether or not the server answered
                             just now, so a failed read is a note above the list,
                             not an error page in place of it. -->
                        <div v-if="toolsError" class="mcp-test mcp-test-bad mb-3">
                            <i class="fa-solid fa-circle-exclamation mt-1"></i>
                            <div>
                                <p class="mb-0">{{ toolsError }}</p>
                                <p v-if="toolsStale" class="mcp-test-tools mb-0">
                                    Showing the tools from
                                    {{ DateTimeFormat(toolsFetchedAt) }}. The permissions
                                    below are still in force.
                                </p>
                            </div>
                        </div>

                        <div class="mcp-default-row">
                            <div class="mcp-tool-text">
                                <p class="mcp-tool-name mb-0">Default for every tool</p>
                                <p class="settings-help mb-0">
                                    {{ MODE_HELP[draft.default] }}
                                    Choosing one here sets every tool below to it —
                                    including tools this server adds later.
                                </p>
                            </div>

                            <div class="mcp-modes" role="group" aria-label="Default permission">
                                <button v-for="option in MODE_OPTIONS" :key="option.value" type="button"
                                    class="mcp-mode" :class="[
                                        `mcp-mode-${option.value}`,
                                        { active: draft.default === option.value },
                                    ]" :disabled="!canEditPolicy" @click="setDefaultMode(option.value)">
                                    <i class="fa-solid" :class="option.icon" aria-hidden="true"></i>
                                    <span style="padding-top: 0.1rem;">{{ option.label }}</span>
                                </button>
                            </div>
                        </div>

                        <div v-if="!tools.length" class="mcp-empty mt-4">
                            <span class="mcp-empty-icon"><i class="fa-solid fa-screwdriver-wrench"></i></span>
                            <p class="mcp-empty-title mb-1">No tools to show</p>
                            <p class="settings-help mb-0">
                                <template v-if="toolsError">
                                    The server could not be read, so its tools are not
                                    listed. The default above still applies to whatever
                                    it offers.
                                </template>
                                <template v-else>
                                    This server reported no tools. Press Refresh tools
                                    once it has some.
                                </template>
                            </p>
                        </div>

                        <template v-else>
                            <p class="mcp-eyebrow mt-4">
                                {{ tools.length }} {{ tools.length === 1 ? 'tool' : 'tools' }}
                                <span v-if="overrideCount" class="mcp-eyebrow-note">
                                    ·
                                    <template v-if="policyIsDerived">
                                        {{ overrideCount }} read-only, allowed by default
                                    </template>
                                    <template v-else>{{ overrideCount }} set individually</template>
                                </span>
                            </p>

                            <ul class="mcp-tools">
                                <li v-for="tool in tools" :key="tool.name" class="mcp-tool">
                                    <div class="mcp-tool-text">
                                        <p class="mcp-tool-name mb-0">
                                            {{ tool.title || tool.name }}
                                            <span v-if="toolRiskTag(tool)" class="mcp-tag" :class="toolRiskTag(tool).class">
                                                {{ toolRiskTag(tool).label }}
                                            </span>
                                        </p>
                                        <p v-if="tool.description" class="mcp-tool-desc mb-0">
                                            {{ tool.description }}
                                        </p>
                                    </div>

                                    <div class="mcp-modes" role="group" :aria-label="`Permission for ${tool.name}`">
                                        <button v-for="option in MODE_OPTIONS" :key="option.value" type="button"
                                            class="mcp-mode" :class="[
                                                `mcp-mode-${option.value}`,
                                                { active: modeOf(tool) === option.value },
                                            ]" :disabled="!canEditPolicy" @click="setToolMode(tool, option.value)">
                                            <i class="fa-solid" :class="option.icon" aria-hidden="true"></i>
                                            <span style="padding-top: 0.1rem;">{{ option.label }}</span>
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </template>
                    </template>
                </div>

                <div class="card-footer" v-if="!toolsLoading || tools.length">
                    <div class="footer-status">
                        <span v-if="policyError" class="text-danger">
                            <i class="fa-solid fa-circle-exclamation me-1"></i>{{ policyError }}
                        </span>
                        <span v-else-if="!canEditPolicy" class="text-muted">
                            <i class="fa fa-lock me-1"></i>
                            Read only — {{ selected.scope === 'shared'
                                ? 'only admins can change a shared server\'s permissions.'
                                : 'only the person who added this server can change its permissions.' }}
                        </span>
                        <span v-else-if="policyDirty" class="text-muted">
                            {{ pendingChangeCount }}
                            {{ pendingChangeCount === 1 ? 'change' : 'changes' }} not saved
                        </span>
                        <span v-else-if="policyIsStored" class="text-muted">
                            Last changed {{ DateTimeFormat(policy.updatedAt) }}
                            <template v-if="policy.updatedBy"> by {{ policy.updatedBy }}</template>
                        </span>
                        <!-- Nothing stored. Say so, and say what is deciding
                             instead — for a built-in that is the tool's own
                             annotation, so "every tool asks" would be wrong. -->
                        <span v-else-if="policyIsDerived" class="text-muted">
                            Not set — each tool follows what it does: reads run,
                            anything that makes changes asks first
                        </span>
                        <span v-else class="text-muted">
                            Not set — every tool asks before it runs
                        </span>
                    </div>
                    <div class="d-flex gap-2" v-if="canEditPolicy">
                        <!-- Only offered once there is something stored to
                             remove: on an unconfigured server this is already
                             the state, and a button that does nothing invites
                             the click that proves it. -->
                        <button v-if="policyIsStored" class="btn btn-outline-danger btn-sm"
                            :disabled="savingPolicy || resettingPolicy" @click="confirmResetPolicy">
                            <span v-if="resettingPolicy" class="spinner-border spinner-border-sm me-1" role="status"
                                style="width:1em; height:1em">
                                <span class="visually-hidden">Resetting</span>
                            </span>
                            Reset to defaults
                        </button>
                        <button class="btn btn-outline-secondary btn-sm"
                            :disabled="!policyDirty || savingPolicy || resettingPolicy" @click="resetPolicyDraft">
                            Discard
                        </button>
                        <button class="btn btn-primary btn-sm"
                            :disabled="!policyDirty || savingPolicy || resettingPolicy" @click="savePolicy">
                            <span v-if="savingPolicy" class="spinner-border spinner-border-sm me-1" role="status"
                                style="width:1em; height:1em">
                                <span class="visually-hidden">Saving</span>
                            </span>
                            Save permissions
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { DateTimeFormat } from '@/utils/commonFunction';
import { McpOAuthCancelled, runOAuthConnect } from '@/utils/mcpOauthPopup';

/**
 * Auth types the agents service accepts but cannot yet complete a sign-in for.
 * Kept in sync with `services/auth_providers/factory.make_auth_provider`, which
 * skips these servers at chat time — so showing them as usable would be a lie.
 *
 * `oauth_cimd` left this list when the connect flow shipped; it covers both
 * CIMD and the deprecated DCR fallback, which differ only in how the client id
 * is obtained and are settled server-side before the user sees anything.
 */
const UNSUPPORTED_AUTH_TYPES = ['ema', 'xaa', 'aws_iam'];

/** Auth types the user completes by signing in rather than by pasting a secret. */
const OAUTH_AUTH_TYPES = ['oauth_cimd'];

const AUTH_LABELS = {
    none: 'No sign-in',
    bearer_passthrough: 'Your Aqfer sign-in',
    api_key: 'API key',
    oauth_cimd: 'OAuth',
    ema: 'Enterprise sign-in',
    xaa: 'Enterprise sign-in',
    aws_iam: 'AWS IAM',
};

const STATUS_LABELS = {
    ready: 'In use',
    attention: 'Needs setup',
    blocked: 'Unavailable',
    off: 'Off',
};

/** Plural nouns for the tally above the list. Order sets the reading order. */
const TALLY_LABELS = [
    ['ready', 'in use'],
    ['attention', 'need setup'],
    ['blocked', 'unavailable'],
    ['off', 'off'],
];

/**
 * The three permission modes, in escalating-restriction order so the control
 * reads left-to-right the way the sentence does.
 */
const MODE_OPTIONS = [
    { value: 'allow', label: 'Allow', icon: 'fa-circle-check' },
    { value: 'ask', label: 'Ask', icon: 'fa-hand' },
    { value: 'deny', label: 'Deny', icon: 'fa-ban' },
];

/** What each default actually means, said once so the card never has to guess. */
const MODE_HELP = {
    allow: 'Tools run without asking.',
    ask: 'PAICE asks in the chat every time a tool is used.',
    deny: 'Tools are withheld from PAICE entirely.',
};

const DEFAULT_MODE = 'ask';

const VERDICT_ICONS = {
    ready: 'fa-circle-check',
    attention: 'fa-circle-exclamation',
    blocked: 'fa-ban',
    off: 'fa-circle-minus',
};

export default {
    name: 'McpServers',
    props: {
        /** Bumped by the settings page's Refresh button. */
        reloadToken: { type: Number, default: 0 },
    },
    // The panel fetches for itself, so the page has no other way to know a
    // refresh it asked for is still running.
    emits: ['update:loading'],
    data() {
        return {
            loading: false,
            loadError: '',
            selectedId: '',

            testing: false,
            testResult: null,
            togglingEnabled: false,

            apiKeyDraft: '',
            apiKeyRevealed: false,
            credentialLoading: false,
            savingCredential: false,
            credentialError: '',
            connectingOAuth: false,

            MODE_OPTIONS,
            MODE_HELP,
            toolsLoading: false,
            savingPolicy: false,
            resettingPolicy: false,
            policyError: '',
            // The edit in progress. Seeded from the saved policy every time a
            // server is opened or its policy is re-read, so "Discard changes"
            // is just a re-seed and nothing has to track individual undo.
            draft: { default: DEFAULT_MODE, tools: {} },
        };
    },
    computed: {
        cid() {
            return this.$store.state.currentClient?.cid;
        },
        servers() {
            return this.$store.state.paice.mcpServers || [];
        },
        /** Aqfer's own servers, which are always present and cannot be removed. */
        builtinServers() {
            return this.servers.filter((s) => s.builtin);
        },
        /** Everything the user added themselves — a built-in is not "added". */
        externalServers() {
            return this.servers.filter((s) => !s.builtin);
        },
        /**
         * The list in two sections. Aqfer's own servers lead because they are
         * the ones that are already there; anything else was connected by hand
         * and answers a different question. An empty section is dropped — the
         * external one has its own invitation below the list.
         */
        serverGroups() {
            return [
                { key: 'aqfer', title: 'Aqfer', servers: this.builtinServers },
                { key: 'external', title: 'External', servers: this.externalServers },
            ].filter((group) => group.servers.length);
        },
        /** The cid's switched-on set — the same for every user in the client. */
        enabledIds() {
            return this.$store.state.paice.mcpEnabledIds || [];
        },
        credentials() {
            return this.$store.state.paice.mcpCredentials || {};
        },
        selected() {
            return this.servers.find((s) => s.serverId === this.selectedId) || null;
        },
        isAdmin() {
            const allowed = this.$store.state.paice.privilegedRoles || [];
            return this.$store.getters.userRoles.isAccessByRole(allowed);
        },
        currentUserEmail() {
            return this.$store.state.currentUser?.email || '';
        },
        credentialSaved() {
            const cred = this.credentials[this.selectedId];
            return !!cred && (cred.hasApiKey || cred.status === 'connected');
        },
        /**
         * Connected and usable. Note this stays true once `expiresAt` is in the
         * past, as long as the connection can renew itself: an expired *access
         * token* is the ordinary state between chat turns, and the service
         * refreshes it before the next one. Saying "disconnected" there would
         * send the user through a consent screen for nothing.
         */
        oauthConnected() {
            const cred = this.credentials[this.selectedId];
            return cred?.status === 'connected' && !cred?.needsReauth;
        },
        /**
         * Only the user can fix this one. Either the service already tried to
         * refresh and the authorization server refused, or the access token ran
         * out with no refresh token to recover with.
         *
         * The service decides — `needsReauth` — rather than the browser
         * comparing `expiresAt` to the clock, because that comparison is wrong
         * on its own (it cannot see whether a refresh token exists) and because
         * a second copy of the rule is a second thing to drift.
         */
        oauthNeedsReauth() {
            return !!this.credentials[this.selectedId]?.needsReauth;
        },
        /**
         * Past its access-token expiry but able to renew itself. Not a problem
         * — surfaced only so the card can say so rather than looking stale.
         */
        oauthRenewsAutomatically() {
            const cred = this.credentials[this.selectedId];
            if (!cred || cred.needsReauth || cred.status !== 'connected') return false;
            if (!cred.expiresAt) return false;
            return new Date(cred.expiresAt).getTime() <= Date.now();
        },
        oauthScopes() {
            return this.credentials[this.selectedId]?.scopes || [];
        },
        /**
         * How many servers land in each status. Zero-count statuses are dropped
         * rather than shown as "0 off" — the line reports what is there.
         */
        tally() {
            const counts = {};
            this.servers.forEach((s) => {
                const status = this.statusOf(s);
                counts[status] = (counts[status] || 0) + 1;
            });
            return TALLY_LABELS
                .filter(([status]) => counts[status])
                .map(([status, label]) => ({ status, label, count: counts[status] }));
        },
        /**
         * The detail page's opening claim: what PAICE will do the next time it
         * reaches for this server, and — when that is nothing — what to do about
         * it. Same source of truth as the rail on the list row.
         */
        verdict() {
            const status = this.selected ? this.statusOf(this.selected) : 'off';
            const builtin = !!this.selected?.builtin;
            const copy = {
                ready: {
                    title: 'PAICE can use this server',
                    detail: 'Its tools are offered in every chat on this client. Press Test '
                        + 'connection to see what it answers with.',
                },
                attention: {
                    title: 'PAICE will skip this server',
                    detail: this.missingCredentialDetail(),
                },
                blocked: {
                    title: 'PAICE cannot reach this server',
                    // The service names the built-in it could not resolve, so its
                    // reason is better than anything this can compose.
                    detail: builtin
                        ? this.selected?.unavailableReason
                            || 'This server is not deployed for this client.'
                        : `${this.selected ? this.authLabel(this.selected) : 'This'} sign-in is not available in `
                            + 'this release, so nothing here takes effect yet.',
                },
                off: {
                    title: 'PAICE is not using this server',
                    detail: 'Enable it under Availability to offer its tools to '
                        + 'everyone on this client.',
                },
            }[status];
            return { status, icon: VERDICT_ICONS[status], ...copy };
        },
        /** The tools endpoint's whole response for the open server. */
        toolsPayload() {
            return this.$store.state.paice.mcpServerTools[this.selectedId] || null;
        },
        tools() {
            return this.toolsPayload?.tools || [];
        },
        policy() {
            return this.toolsPayload?.policy || null;
        },
        toolsError() {
            return this.toolsPayload?.error || '';
        },
        toolsStale() {
            return !!this.toolsPayload?.stale;
        },
        toolsFetchedAt() {
            return this.toolsPayload?.fetchedAt || null;
        },
        /**
         * The service decides this, not the browser, and it is reported on the
         * policy rather than inferred — the rule (admins for shared servers,
         * the owner for private ones) then lives in exactly one place.
         */
        canEditPolicy() {
            return !!this.policy?.canEdit;
        },
        /** Tools whose setting differs from the default — the ones worth counting. */
        overrideCount() {
            return Object.keys(this.draft.tools).length;
        },
        /**
         * Whether the per-tool entries were computed by the service rather than
         * chosen by an admin — a built-in whose permissions nobody has saved,
         * where a read-only tool is allowed on the strength of its own
         * annotation. They are real (a chat will honour them) but calling them
         * "set individually" would credit a person who never touched them.
         */
        policyIsDerived() {
            return !!this.policy?.derived;
        },
        /**
         * Whether an admin has ever saved permissions for this server.
         *
         * `updatedAt` is the tell, and the only one: the service omits it for
         * the never-configured case and always sets it on a stored row. Derived
         * entries do not count — nobody chose those, which is the whole reason
         * they are flagged separately.
         */
        policyIsStored() {
            return !!this.policy?.updatedAt;
        },
        policyDirty() {
            if (!this.policy) return false;
            if (this.draft.default !== this.policy.default) return true;
            const saved = this.policy.tools || {};
            const keys = new Set([...Object.keys(saved), ...Object.keys(this.draft.tools)]);
            return [...keys].some((k) => saved[k] !== this.draft.tools[k]);
        },
        pendingChangeCount() {
            if (!this.policy) return 0;
            const saved = this.policy.tools || {};
            const keys = new Set([...Object.keys(saved), ...Object.keys(this.draft.tools)]);
            const toolChanges = [...keys].filter((k) => saved[k] !== this.draft.tools[k]).length;
            return toolChanges + (this.draft.default !== this.policy.default ? 1 : 0);
        },
        /**
         * Whether the Availability switch is live for this caller.
         *
         * The service decides and reports it rather than the browser
         * re-deriving "admin, unless it is my own private server" — and it is
         * not the same as `editable`: a built-in has no definition to edit
         * but an admin can still switch it off for the client.
         */
        canToggleEnabled() {
            return !!this.selected?.enabledEditable;
        },
        /** Shared keys are an admin decision; a personal key is always yours. */
        canSetCredential() {
            if (!this.selected) return false;
            return this.selected.credentialOwnership === 'shared' ? this.isAdmin : true;
        },
    },
    watch: {
        cid() {
            this.selectedId = '';
            this.load({ force: true });
        },
        reloadToken() {
            this.load({ force: true });
        },
        /** Opening a server re-reads its credential — the list view may be stale. */
        selectedId(id) {
            this.testResult = null;
            this.apiKeyDraft = '';
            this.apiKeyRevealed = false;
            this.credentialError = '';
            this.policyError = '';
            this.draft = { default: DEFAULT_MODE, tools: {} };
            if (id) {
                this.loadCredential(id);
                this.loadTools();
            }
        },
        /** A re-read replaces the saved policy, so the draft is re-seeded from it. */
        policy: {
            deep: true,
            handler() {
                this.resetPolicyDraft();
            },
        },
    },
    created() {
        this.load();
    },
    unmounted() {
        // Leaving the tab mid-load would otherwise strand the parent's spinner
        // on a fetch whose result no longer has anywhere to land.
        if (this.loading) this.$emit('update:loading', false);
    },
    methods: {
        DateTimeFormat,
        ...mapActions(['showModal', 'hideModal', 'addToast']),
        ...mapActions('paice', [
            'getMcpServers', 'getMcpCredential', 'deleteMcpServer',
            'saveMcpCredential', 'deleteMcpCredential', 'setMcpServerEnabled', 'testMcpServer',
            'startMcpOAuth', 'completeMcpOAuth',
            'getMcpServerTools', 'saveMcpToolPolicy', 'resetMcpToolPolicy',
        ]),

        async load({ force = false } = {}) {
            this.setLoading(true);
            this.loadError = '';
            try {
                const servers = await this.getMcpServers({ forceGet: force });
                // The rail on each row claims whether PAICE can use that server
                // right now, which is only true if we know its credential state.
                // Fetched in parallel and per-server tolerant: one server whose
                // status cannot be read should not blank out the whole list.
                await Promise.allSettled(
                    servers.filter((s) => this.needsCredential(s))
                        .map((s) => this.getMcpCredential(s.serverId)),
                );
            } catch (e) {
                this.loadError = this.errorMessage(e, 'Could not load MCP servers.');
            } finally {
                this.setLoading(false);
            }
        },

        /** Kept in step with the parent so its Refresh button can reflect it. */
        setLoading(value) {
            this.loading = value;
            this.$emit('update:loading', value);
        },

        async loadCredential(serverId) {
            const server = this.servers.find((s) => s.serverId === serverId);
            if (!server || !this.needsCredential(server)) return;
            this.credentialLoading = true;
            try {
                await this.getMcpCredential(serverId);
            } catch (e) {
                this.credentialError = this.errorMessage(e, 'Could not read the key status.');
            } finally {
                this.credentialLoading = false;
            }
        },

        open(server) {
            this.selectedId = server.serverId;
        },
        close() {
            this.selectedId = '';
        },

        // ── Derived state ────────────────────────────────────────────────────

        /**
         * What will actually happen when PAICE next reaches for this server.
         *
         * Mirrors `build_mcp_clients` on the service side rather than the stored
         * config: a server can be switched on and still be skipped, and saying
         * "on" in that case would be wrong.
         */
        statusOf(server) {
            // `available: false` means the service could not resolve an endpoint
            // at all — today only a built-in, in a cid with no such deployment.
            if (server.available === false) return 'blocked';
            if (UNSUPPORTED_AUTH_TYPES.includes(server.authType)) return 'blocked';
            if (!this.isEnabled(server)) return 'off';
            if (this.needsCredential(server) && !this.hasCredential(server)) return 'attention';
            return 'ready';
        },
        statusLabel(server) {
            return STATUS_LABELS[this.statusOf(server)];
        },
        /**
         * The status this server would land in if it were switched on right now
         * — what a confirmation dialog has to promise, since "enabled" and "in
         * use" are not the same thing when a key is missing.
         */
        statusIfEnabled(server) {
            if (server.available === false) return 'blocked';
            if (UNSUPPORTED_AUTH_TYPES.includes(server.authType)) return 'blocked';
            if (this.needsCredential(server) && !this.hasCredential(server)) return 'attention';
            return 'ready';
        },
        /** A status as a pill for a confirmation dialog's before/after row. */
        statusState(status) {
            return {
                tone: status,
                icon: VERDICT_ICONS[status],
                label: STATUS_LABELS[status],
            };
        },
        /**
         * Which server a dialog is about — the full endpoint rather than just
         * its host, since this is the one place the question "is this the right
         * server?" has to be answerable. The cid is not a row: it is already in
         * the sentence above, and the page has never been about another client.
         */
        serverFacts(server) {
            const facts = [{ label: 'Server', value: server.name }];
            if (server.url) facts.push({ label: 'Endpoint', value: server.url, mono: true });
            return facts;
        },
        /** A permission mode as the same kind of pill, in the list's colours. */
        modeState(mode) {
            const option = MODE_OPTIONS.find((o) => o.value === mode)
                || MODE_OPTIONS.find((o) => o.value === DEFAULT_MODE);
            return { tone: option.value, icon: option.icon, label: option.label };
        },
        /**
         * Why an enabled server is still being skipped. Always a missing
         * credential — that is the only route into `attention` — but which one,
         * and how it is supplied, differs by auth type: an OAuth server is
         * signed in to, not pasted into, and a shared key is an admin's job.
         */
        missingCredentialDetail() {
            const server = this.selected;
            if (this.isOAuth(server)) {
                return this.oauthNeedsReauth
                    ? 'It is enabled, but your sign-in has expired. Connect '
                        + 'again below.'
                    : 'It is enabled, but you have not signed in to it yet. Connect '
                        + 'below.';
            }
            return server?.credentialOwnership === 'shared'
                ? `It is enabled, but no shared API key is saved yet. An admin for `
                    + `${this.cid} adds one below.`
                : 'It is enabled, but you have not saved your API key yet. Add one below.';
        },
        isEnabled(server) {
            return this.enabledIds.includes(server.serverId);
        },
        /** A server only its owner can see — so "on for the cid" is "on for me". */
        isPrivate(server) {
            return !server.builtin && server.scope !== 'shared';
        },
        /** Infrastructure auth derives its credential per request; nothing to store. */
        needsCredential(server) {
            return server.credentialOwnership !== 'infrastructure';
        },
        /** Completed by signing in at the server rather than by pasting a secret. */
        isOAuth(server) {
            return OAUTH_AUTH_TYPES.includes(server?.authType);
        },
        hasCredential(server) {
            const cred = this.credentials[server.serverId];
            if (!cred) return false;
            // A credential the user has to re-authorize does not count: the row
            // exists but the grant behind it is dead, and the chat path will
            // skip the server. Covers both `status: expired` and an access
            // token that ran out with no refresh token.
            if (cred.needsReauth) return false;
            return cred.hasApiKey || cred.status === 'connected';
        },
        /**
         * Whether Edit and Delete are offered. The service decides and reports
         * it, rather than the browser re-deriving the rule from scope and
         * ownership — and it is the only honest answer for a built-in, which
         * has no stored definition to edit and cannot be removed.
         */
        canModify(server) {
            return !!server.editable;
        },
        authLabel(server) {
            return AUTH_LABELS[server.authType] || server.authType;
        },
        hostOf(url) {
            try {
                return new URL(url).host;
            } catch (e) {
                return url;
            }
        },

        // ── Actions ──────────────────────────────────────────────────────────

        /**
         * Flip the switch — for the whole client, so a shared server asks first.
         *
         * The confirmation is not ceremony: this adds or removes tools for every
         * PAICE user on the cid, and there is no personal copy of the setting
         * underneath that would soften a mistake. A private server is nobody
         * else's business, so that one flips straight away.
         */
        toggleEnabled(event) {
            const wantOn = event.target.checked;
            const server = this.selected;
            const input = event.target;

            if (this.isPrivate(server)) {
                return this.applyEnabled(server, wantOn, input);
            }

            // Put the switch back before asking, and let the answer drive it.
            // The dialog can also be dismissed with its close button, which
            // reports nothing back — so the control must not be left claiming a
            // change that was never made.
            input.checked = !wantOn;
            this.showModal({
                component: 'PaiceConfirm',
                // The dialog dismisses itself once this settles, so the confirm
                // has to resolve either way — applyEnabled reports its own.
                data: wantOn
                    ? this.enableDialog(server)
                    : this.disableDialog(server),
            });
            return undefined;
        },

        /**
         * What switching a shared server on actually does, said as a change
         * rather than a sentence: which status it moves to, how many tools that
         * adds, and the two things people ask about — whether permissions come
         * with it, and whether it will work without a key.
         */
        enableDialog(server) {
            const next = this.statusIfEnabled(server);
            const points = [];
            if (this.tools.length) {
                points.push({
                    icon: 'fa-screwdriver-wrench',
                    text: `${this.tools.length} `
                        + `${this.tools.length === 1 ? 'tool becomes' : 'tools become'} `
                        + 'available to PAICE.',
                });
            }
            points.push({
                icon: 'fa-hand',
                tone: 'keep',
                text: 'Tool permissions are unchanged — anything set to Ask still '
                    + 'asks before it runs.',
            });
            if (next === 'attention') {
                points.push({
                    icon: this.isOAuth(server) ? 'fa-right-to-bracket' : 'fa-key',
                    tone: 'danger',
                    text: this.isOAuth(server)
                        ? 'Nobody has signed in to it yet, so PAICE will keep skipping '
                            + 'it until each person connects their own account.'
                        : 'No API key is saved yet, so PAICE will keep skipping it '
                            + 'until one is.',
                });
            }
            return {
                tone: 'primary',
                title: `Enable ${server.name} for everyone?`,
                facts: this.serverFacts(server),
                lede: `PAICE will offer this server's tools in every chat on `
                    + `${this.cid} — not just yours.`,
                shift: {
                    from: this.statusState(this.statusOf(server)),
                    to: this.statusState(next),
                },
                points,
                scope: 'Everyone on this client sees the change from their next '
                    + 'message. There is no personal copy of this switch.',
                confirmText: 'Enable',
                confirm: () => this.applyEnabled(server, true),
            };
        },

        /**
         * The same, the other way. What survives is the part worth stating: the
         * fear here is that switching off throws away the key and the
         * permissions, and it does not.
         */
        disableDialog(server) {
            return {
                // Red, not amber: this takes tools away from everyone on the
                // client, and that is what every other button in the portal
                // that takes something away looks like.
                tone: 'danger',
                title: `Disable ${server.name} for everyone?`,
                facts: this.serverFacts(server),
                lede: `PAICE will stop using this server in every chat on `
                    + `${this.cid} — not just yours.`,
                shift: {
                    from: this.statusState(this.statusOf(server)),
                    to: this.statusState('off'),
                },
                points: [
                    {
                        icon: 'fa-comment-slash',
                        tone: 'danger',
                        text: 'Chats in progress lose these tools mid-conversation.',
                    },
                    {
                        icon: 'fa-box-archive',
                        tone: 'keep',
                        text: 'Saved keys and tool permissions are kept — switching it '
                            + 'back on restores them.',
                    },
                ],
                scope: 'Everyone on this client sees the change from their next '
                    + 'message. There is no personal copy of this switch.',
                confirmText: 'Disable',
                confirm: () => this.applyEnabled(server, false),
            };
        },

        /**
         * Persist one server's on/off state. Never rejects: the toast is the
         * report, and the confirmation dialog is waiting on this to settle.
         */
        async applyEnabled(server, wantOn, input = null) {
            this.togglingEnabled = true;
            try {
                await this.setMcpServerEnabled({
                    serverId: server.serverId,
                    enabled: wantOn,
                });
                this.addToast({
                    type: 'success',
                    message: `${server.name} ${wantOn ? 'enabled' : 'disabled'}`,
                });
            } catch (e) {
                // Put the switch back: the store still holds the old set, so
                // re-reading it is enough to undo the optimistic flip.
                if (input) input.checked = !wantOn;
                this.addToast({
                    type: 'error',
                    message: this.errorMessage(e, 'Could not change availability.'),
                });
            } finally {
                this.togglingEnabled = false;
            }
        },

        /**
         * Connect an OAuth server: sign in at the server, then hand the result back.
         *
         * The popup is opened inside this click handler, before anything is
         * awaited — browsers only permit `window.open` synchronously within a
         * user gesture, so fetching the URL first would get the window blocked.
         * `runOAuthConnect` handles that ordering.
         *
         * `iss` is forwarded to the service untouched. The service compares it
         * against the issuer it recorded at discovery *before* it redeems the
         * code (RFC 9207); dropping it here would quietly disable the check that
         * closes authorization-server mix-up attacks.
         */
        async connectOAuth(server) {
            this.connectingOAuth = true;
            this.credentialError = '';
            // Captured once, up front. The consent screen can sit open for
            // minutes and the client picker stays live behind it; both halves of
            // the exchange have to address the same partition, and the pending
            // PKCE row was written under this cid.
            const cid = this.cid;
            try {
                const { code, state, iss } = await runOAuthConnect({
                    start: () => this.startMcpOAuth({ serverId: server.serverId, cid }),
                });
                await this.completeMcpOAuth({ serverId: server.serverId, cid, code, state, iss });
                this.addToast({ type: 'success', message: `Connected to ${server.name}` });
                // The catalog is only readable once there is a token, so this is
                // the first moment the tool list can be populated.
                await this.loadTools({ refresh: true });
            } catch (e) {
                if (e instanceof McpOAuthCancelled) {
                    // Closing the window is a decision, not a failure. The
                    // pending row expires on its own server-side.
                    return;
                }
                this.credentialError = this.errorMessage(e, 'Could not connect to the server.');
                this.addToast({ type: 'error', message: this.credentialError });
            } finally {
                this.connectingOAuth = false;
            }
        },

        async test() {
            this.testing = true;
            this.testResult = null;
            try {
                this.testResult = await this.testMcpServer({ serverId: this.selected.serverId });
            } catch (e) {
                // 428/501 are the service telling us the server cannot be dialled
                // as configured — a real answer, so it is shown in the same place
                // a connection failure would be.
                this.testResult = {
                    ok: false,
                    elapsedMs: 0,
                    error: this.errorMessage(e, 'Could not test the connection.'),
                };
            } finally {
                this.testing = false;
            }
        },

        /**
         * Keys arrive by paste, and a paste out of a terminal or a mail client
         * brings whitespace an HTTP header cannot carry. Strip it as it is
         * typed rather than trimming at save, so what the reveal toggle shows
         * is exactly what gets stored — and a stray newline never becomes a
         * 401 to debug later. The DOM value is rewritten too: stripping can
         * leave the model unchanged, and then Vue has no reason to re-render.
         */
        onApiKeyInput(event) {
            const cleaned = event.target.value.replace(/\s+/g, '');
            if (event.target.value !== cleaned) event.target.value = cleaned;
            this.apiKeyDraft = cleaned;
        },
        /** Enter in the field does what the Save button does, or nothing. */
        submitKey() {
            if (!this.apiKeyDraft || !this.canSetCredential || this.savingCredential) return;
            this.saveCredential();
        },
        async saveCredential() {
            this.savingCredential = true;
            this.credentialError = '';
            try {
                await this.saveMcpCredential({
                    serverId: this.selected.serverId,
                    apiKey: this.apiKeyDraft.trim(),
                });
                this.apiKeyDraft = '';
                this.apiKeyRevealed = false;
                this.addToast({ type: 'success', message: 'Key saved' });
            } catch (e) {
                this.credentialError = e?.response?.status === 503
                    ? 'This environment cannot store keys yet — credential encryption is not set up. Ask your Aqfer contact to enable it.'
                    : this.errorMessage(e, 'Could not save the key.');
            } finally {
                this.savingCredential = false;
            }
        },

        /**
         * Ask before removing the key.
         *
         * A stored key is never shown again, so this is not an edit that can be
         * undone by retyping what was there — and for a shared key it is the
         * only one the client has, which is worth saying before it is gone.
         */
        confirmRevokeCredential() {
            const server = this.selected;
            const shared = server.credentialOwnership === 'shared';
            const oauth = this.isOAuth(server);
            const points = [{
                icon: 'fa-plug-circle-exclamation',
                tone: 'danger',
                text: oauth
                    ? 'PAICE will skip this server in chat until you sign in again.'
                    : 'PAICE will skip this server in chat until a new key is saved.',
            }];
            if (shared) {
                points.push({
                    icon: 'fa-users',
                    tone: 'danger',
                    text: `Everyone on ${this.cid} uses this key — it is the only one `
                        + 'this client has.',
                });
            }
            points.push(oauth
                ? {
                    icon: 'fa-arrow-rotate-left',
                    tone: 'keep',
                    // Deliberately not promised as a revocation at the server:
                    // the service deletes its own tokens, and whether the grant
                    // is torn down upstream is the server's business.
                    text: `Reconnecting takes one sign-in. This removes the tokens PAICE `
                        + `holds. Any access you granted at ${this.hostOf(server.url) || 'the server'} `
                        + 'should be reviewed there.',
                }
                : {
                    icon: 'fa-eye-slash',
                    tone: 'keep',
                    text: 'The stored key is never shown, so it cannot be put back — '
                        + "you'll need a fresh one from the server.",
                });
            this.showModal({
                component: 'PaiceConfirm',
                data: {
                        tone: 'danger',
                    title: oauth ? 'Disconnect this server?' : 'Remove the saved key?',
                    facts: this.serverFacts(server),
                    lede: (() => {
                        if (oauth) return `PAICE will lose your connection to ${server.name}.`;
                        return shared
                            ? `PAICE will lose the key it uses for everyone on ${this.cid}.`
                            : `PAICE will lose your key for ${server.name}.`;
                    })(),
                    shift: {
                        from: this.statusState(this.statusOf(server)),
                        to: this.statusState(this.isEnabled(server) ? 'attention' : 'off'),
                    },
                    points,
                    scope: (() => {
                        // An OAuth grant is always per person — the service
                        // rejects oauth + shared outright — so it never reads
                        // as a client-wide setting here.
                        if (oauth) {
                            return 'This is your own sign-in. Other people on this client keep theirs.';
                        }
                        return shared
                            ? 'The key is a client setting — only an admin can save a new one.'
                            : 'This is your own key. Other people on this client keep theirs.';
                    })(),
                    scopeIcon: (shared && !oauth) ? 'fa-users' : 'fa-user',
                    irreversible: true,
                    confirmText: oauth ? 'Disconnect' : 'Remove key',
                    // Resolves either way — applyRevokeCredential reports its own.
                    confirm: () => this.applyRevokeCredential(server.serverId),
                },
            });
        },

        /**
         * Delete the stored key. Never rejects: the dialog is waiting on this to
         * settle, and the card's footer (plus a toast) is the report.
         */
        async applyRevokeCredential(serverId) {
            this.savingCredential = true;
            this.credentialError = '';
            try {
                await this.deleteMcpCredential(serverId);
                this.addToast({ type: 'success', message: 'Key removed' });
            } catch (e) {
                const message = this.errorMessage(e, 'Could not remove the key.');
                if (this.selectedId === serverId) this.credentialError = message;
                this.addToast({ type: 'error', message });
            } finally {
                this.savingCredential = false;
            }
        },

        // ── Tool permissions ─────────────────────────────────────────────────

        /**
         * Read the open server's tools and policy.
         *
         * `refresh` re-dials the MCP server; without it the service answers from
         * its cached catalog, which is what makes reopening a server instant.
         * A server that cannot be reached is not an error here — the response
         * still carries the policy, which is in force regardless.
         */
        async loadTools({ refresh = false } = {}) {
            const serverId = this.selectedId;
            if (!serverId) return;
            this.toolsLoading = true;
            this.policyError = '';
            try {
                await this.getMcpServerTools({ serverId, refresh });
                // Guard against a slow response landing after the user has moved
                // on: seeding the draft then would overwrite edits on a
                // different server.
                if (this.selectedId === serverId) this.resetPolicyDraft();
            } catch (e) {
                this.policyError = this.errorMessage(e, 'Could not read this server\'s tools.');
            } finally {
                if (this.selectedId === serverId) this.toolsLoading = false;
            }
        },

        /** Re-seed the draft from what is stored. Also the Discard changes button. */
        resetPolicyDraft() {
            this.draft = {
                default: this.policy?.default || DEFAULT_MODE,
                tools: { ...(this.policy?.tools || {}) },
            };
            this.policyError = '';
        },

        /**
         * The mode a row's control should show: the tool's own setting if it has
         * one, otherwise whatever the default currently is. Read off the draft,
         * not the server's response, so changing the default visibly moves every
         * row that was inheriting it.
         */
        modeOf(tool) {
            return this.draft.tools[tool.name] || this.draft.default;
        },

        /**
         * Set the default for every tool, and drop every per-tool override with
         * it. The control says "default for every tool", so it has to mean it:
         * leaving pinned rows behind made the list disagree with the sentence
         * directly above it. Singling a tool out again afterwards still works.
         */
        setDefaultMode(mode) {
            this.draft = { default: mode, tools: {} };
        },

        /**
         * Set one tool's permission. Choosing the current default *removes* the
         * override rather than pinning it, so a tool the user never singled out
         * keeps following the default if the default later changes.
         */
        setToolMode(tool, mode) {
            const next = { ...this.draft.tools };
            if (mode === this.draft.default) delete next[tool.name];
            else next[tool.name] = mode;
            this.draft = { ...this.draft, tools: next };
        },

        /**
         * MCP servers may annotate a tool as changing state
         * (`readOnlyHint: false` / `destructiveHint: true`). Worth surfacing:
         * it is the difference between a tool that reads a ticket and one that
         * closes it, and it is the main thing someone setting permissions is
         * scanning for. The two hints aren't the same severity, so they get
         * separate tags: `destructiveHint` (e.g. delete) is red, while
         * `readOnlyHint: false` alone (e.g. create/update, nothing lost) is
         * amber.
         */
        toolRiskTag(tool) {
            const a = tool.annotations || {};
            if (a.destructiveHint === true) return { label: 'Destructive', class: 'mcp-tag-danger' };
            if (a.readOnlyHint === false) return { label: 'Makes changes', class: 'mcp-tag-warning' };
            return null;
        },

        async savePolicy() {
            this.savingPolicy = true;
            this.policyError = '';
            try {
                await this.saveMcpToolPolicy({
                    serverId: this.selectedId,
                    defaultMode: this.draft.default,
                    tools: this.draft.tools,
                });
                this.addToast({ type: 'success', message: 'Tool permissions saved' });
            } catch (e) {
                this.policyError = this.errorMessage(e, 'Could not save the permissions.');
            } finally {
                this.savingPolicy = false;
            }
        },

        /**
         * Ask before throwing away the client's stored permissions.
         *
         * Worth a dialog for the same reason the enable switch is: this applies
         * to every PAICE chat on the cid, there is no personal copy underneath
         * to soften it, and what comes back is not "everything asks" — for a
         * built-in the tools that read go back to running without a prompt.
         * Saying which way it moves is the point of the message.
         */
        confirmResetPolicy() {
            const server = this.selected;
            // What replaces the stored default. A built-in falls back to the
            // service's per-tool reading rather than a single mode, so it gets
            // its own pill instead of one of the three.
            const after = server.builtin
                ? { tone: 'neutral', icon: 'fa-scale-balanced', label: 'Follows each tool' }
                : this.modeState(DEFAULT_MODE);

            const points = [];
            if (this.overrideCount && !this.policyIsDerived) {
                points.push({
                    icon: 'fa-list-check',
                    tone: 'danger',
                    text: `${this.overrideCount} `
                        + `${this.overrideCount === 1 ? 'tool' : 'tools'} set individually `
                        + `${this.overrideCount === 1 ? 'loses' : 'lose'} its own setting.`,
                });
            }
            points.push(server.builtin
                ? {
                    icon: 'fa-scale-balanced',
                    text: 'Each tool goes back to following what it does — reads run '
                        + 'without asking, anything that makes changes asks first — '
                        + 'including tools added later.',
                }
                : {
                    icon: 'fa-hand',
                    text: 'Every tool goes back to asking before it runs, including '
                        + 'tools added later.',
                });
            if (this.policyDirty) {
                points.push({
                    icon: 'fa-eraser',
                    tone: 'danger',
                    text: `Your ${this.pendingChangeCount} unsaved `
                        + `${this.pendingChangeCount === 1 ? 'change goes' : 'changes go'} `
                        + 'with them.',
                });
            }

            const before = this.modeState(this.policy?.default || DEFAULT_MODE);
            this.showModal({
                component: 'PaiceConfirm',
                data: {
                        tone: 'danger',
                    title: `Reset permissions for ${server.name}?`,
                    facts: this.serverFacts(server),
                    lede: `The tool permissions saved for ${this.cid} will be removed, `
                        + 'and PAICE goes back to deciding for itself.',
                    // A row that reads "Ask → Ask" only gets in the way; the
                    // points below already say what happens to the tools.
                    shift: before.label === after.label
                        ? null
                        : { from: before, to: after },
                    points,
                    scope: this.isPrivate(server)
                        ? 'Only you can see this server, so this only changes your own chats.'
                        : 'Permissions are a client setting — everyone on this client '
                            + 'is affected.',
                    irreversible: true,
                    confirmText: 'Reset permissions',
                    // The dialog dismisses itself once this settles, so it has
                    // to resolve either way — applyPolicyReset reports its own.
                    confirm: () => this.applyPolicyReset(server.serverId),
                },
            });
        },

        /**
         * Delete the stored policy and re-read the tools.
         *
         * Never rejects: the confirmation dialog is waiting on this to settle,
         * and the toast (or the footer's error line) is the report.
         */
        async applyPolicyReset(serverId) {
            this.resettingPolicy = true;
            this.policyError = '';
            try {
                await this.resetMcpToolPolicy({ serverId });
                // No re-seed here: the action re-reads the policy, and the
                // `policy` watcher is what turns a new one into a fresh draft.
                this.addToast({ type: 'success', message: 'Tool permissions reset to defaults' });
            } catch (e) {
                const message = this.errorMessage(e, 'Could not reset the permissions.');
                if (this.selectedId === serverId) this.policyError = message;
                this.addToast({ type: 'error', message });
            } finally {
                this.resettingPolicy = false;
            }
        },

        openAdd() {
            this.showModal({
                component: 'PaiceMcpServer',
                data: {
                    mode: 'create',
                    canCreateShared: this.isAdmin,
                    onSaved: () => this.load({ force: true }),
                },
            });
        },

        openEdit() {
            this.showModal({
                component: 'PaiceMcpServer',
                data: {
                    mode: 'edit',
                    server: this.selected,
                    canCreateShared: this.isAdmin,
                    onSaved: () => this.load({ force: true }),
                },
            });
        },

        confirmDelete() {
            const server = this.selected;
            const points = [];
            points.push(this.isEnabled(server)
                ? {
                    icon: 'fa-plug-circle-xmark',
                    tone: 'danger',
                    text: `Its tools disappear from every PAICE chat on ${this.cid}.`,
                }
                : {
                    icon: 'fa-plug-circle-xmark',
                    tone: 'keep',
                    text: 'It is already switched off, so no chat in progress loses tools.',
                });
            if (this.needsCredential(server)) {
                points.push({
                    icon: 'fa-key',
                    tone: 'danger',
                    text: server.credentialOwnership === 'shared'
                        ? "The client's saved API key for it is deleted."
                        : 'Every saved API key for it is deleted — everyone\'s, not just yours.',
                });
            }
            if (this.policyIsStored) {
                points.push({
                    icon: 'fa-shield-halved',
                    tone: 'danger',
                    text: 'Its saved tool permissions are deleted with it.',
                });
            }
            points.push({
                icon: 'fa-rotate-right',
                tone: 'keep',
                text: 'Adding it back later means re-entering the endpoint, the key '
                    + 'and the permissions.',
            });

            this.showModal({
                component: 'PaiceConfirm',
                data: {
                        tone: 'danger',
                    title: `Delete ${server.name}?`,
                    facts: this.serverFacts(server),
                    lede: `PAICE will stop using this server, and its configuration is `
                        + `removed from ${this.cid}.`,
                    shift: {
                        from: this.statusState(this.statusOf(server)),
                        to: { tone: 'neutral', icon: 'fa-trash-can', label: 'Deleted' },
                    },
                    points,
                    scope: this.isPrivate(server)
                        ? 'Only you can see this server, so only your chats change.'
                        : 'It is shared with everyone on this client — they all lose it.',
                    irreversible: true,
                    confirmText: 'Delete server',
                    // `confirm` is the key the dialog calls, and it dismisses
                    // itself once this settles — so don't hide the modal here.
                    confirm: async () => {
                        try {
                            await this.deleteMcpServer(server.serverId);
                            this.selectedId = '';
                            this.addToast({ type: 'success', message: 'Server deleted' });
                            this.load({ force: true });
                        } catch (e) {
                            this.addToast({
                                type: 'error',
                                message: this.errorMessage(e, 'Could not delete the server.'),
                            });
                        }
                    },
                },
            });
        },

        errorMessage(e, fallback) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === 'string' && detail) return detail;
            return e?.message || fallback;
        },

    },
};
</script>

<style scoped>
/* ── Page vocabulary ──────────────────────────────────────────────────────────
   `settings-subtitle`, `settings-help` and `settings-loading` are the PAICE
   settings page's own type scale. They are declared there in a `scoped` block,
   which does not reach into a child component — so this panel has to restate
   them or its captions fall back to body text and break the page's rhythm.
   Values match Settings.vue exactly; change them together. */
.settings-subtitle {
    font-size: 0.75rem;
    color: #95aac9;
    margin-top: 0.15rem;
}

/* Same reason the type scale is restated above: Settings.vue's `scoped` card
   rules stop at this component's boundary. The theme pins `.card-header` to a
   fixed 60px (`$card-header-height`), which fits a bare title — these headers
   also carry a subtitle (a server's description runs to a few lines) and a row
   of buttons, so the wrapped text spills out under the header's bottom border.
   Grow with the content, with 60px as the floor. Keep in step with the block of
   the same name in Settings.vue. */
.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    height: auto;
    min-height: 60px;
}

/* The theme gives the first child `flex: 1` but not `min-width: 0`, so a long
   unbreakable string (a server URL, a tool name) would push the header wider
   than the card. */
.card-header > *:first-child {
    min-width: 0;
}

.settings-help {
    font-size: 0.75rem;
    color: #95aac9;
    max-width: 46rem;
    margin-top: 0.15rem;
}

.settings-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    color: #95aac9;
    font-size: 0.85rem;
}

/* Small uppercase run-in heading, matching `.resolved-label` on the Agent tab. */
.mcp-eyebrow {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #95aac9;
    margin: 0 0 0.6rem;
}

/* ── Tally ────────────────────────────────────────────────────────────────────
   The list's answer before it is read: how many servers are in each state,
   in the same colours the rails use. */
.mcp-tally {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 1.1rem;
    padding: 0.55rem 1.5rem;
    border-bottom: 1px solid #edf2f9;
    background: #f9fbfd;
    font-size: 0.72rem;
    color: #95aac9;
}

.mcp-tally-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    white-space: nowrap;
}

.mcp-tally-item strong {
    color: #12263f;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.mcp-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.mcp-dot-ready {
    background: #00a854;
}

.mcp-dot-attention {
    background: #d9822b;
}

.mcp-dot-blocked {
    background: #e63757;
}

.mcp-dot-off {
    background: #d2ddec;
}

/* ── Verdict ──────────────────────────────────────────────────────────────────
   The detail page's opening claim, and the one place it is stated in a
   sentence. Carries the list rail's colour so the two views agree at a glance,
   and always names the next action when the answer is "nothing will happen". */
.mcp-verdict {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.75rem 0.9rem 0.75rem 1.1rem;
    margin-bottom: 1.5rem;
    border-radius: 0.5rem;
    overflow: hidden;
}

.mcp-verdict-rail {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
}

.mcp-verdict>i {
    font-size: 0.85rem;
    margin-top: 0.18rem;
    flex-shrink: 0;
}

.mcp-verdict-text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.mcp-verdict-title {
    font-size: 0.82rem;
    font-weight: 600;
}

.mcp-verdict-detail {
    font-size: 0.75rem;
    opacity: 0.85;
}

.mcp-verdict-ready {
    background: #e6f6ee;
    color: #1c6f4d;
}

.mcp-verdict-ready .mcp-verdict-rail {
    background: #00a854;
}

.mcp-verdict-attention {
    background: #fbf0e3;
    color: #8f5717;
}

.mcp-verdict-attention .mcp-verdict-rail {
    background: #d9822b;
}

.mcp-verdict-blocked {
    background: #fdeaee;
    color: #b52b45;
}

.mcp-verdict-blocked .mcp-verdict-rail {
    background: #e63757;
}

.mcp-verdict-off {
    background: #f9fbfd;
    color: #6e84a3;
    border: 1px solid #edf2f9;
}

.mcp-verdict-off .mcp-verdict-rail {
    background: #aeb6c3;
}

/* ── The rail ─────────────────────────────────────────────────────────────────
   3px of colour at the left edge of every row, set to what will actually happen
   the next time PAICE reaches for this server — not to what is configured. A
   server can be switched on and still be skipped for a missing key, and the
   rail says so. Never the only signal: the status pill states the same thing in
   words, so it survives both colour blindness and a monochrome screenshot. */
.mcp-rail {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 0 3px 3px 0;
}

.mcp-rail-ready {
    background: #00a854;
}

.mcp-rail-attention {
    background: #d9822b;
}

.mcp-rail-blocked {
    background: #e63757;
}

.mcp-rail-off {
    background: #d2ddec;
}

/* ── List ─────────────────────────────────────────────────────────────────── */
/* Section spacing: the first group runs straight into the card body, later
   ones get air so the heading reads as a break rather than a caption. */
.mcp-group + .mcp-group {
    margin-top: 1.5rem;
}

.mcp-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
}

.mcp-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    text-align: left;
    padding: 0.75rem 0.9rem;
    border: 1px solid #edf2f9;
    border-radius: 0.5rem;
    background: #fff;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mcp-row:hover {
    border-color: #d2ddec;
    box-shadow: 0 2px 6px rgba(18, 38, 63, 0.06);
}

.mcp-row:focus-visible {
    outline: 2px solid #2c7be5;
    outline-offset: 1px;
}

.mcp-row:active {
    border-color: #b6d4fb;
    box-shadow: none;
}

.mcp-row:hover .mcp-chevron {
    color: #95aac9;
}

.mcp-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.45rem;
    background: #f1f4f9;
    color: #6e84a3;
}

.mcp-icon i {
    font-size: 0.8rem;
}

.mcp-icon-off {
    color: #b1c2d9;
}

.mcp-body {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.mcp-title-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.mcp-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #12263f;
}

.mcp-meta {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.7rem;
    color: #95aac9;
    min-width: 0;
}

.mcp-sep {
    color: #9ba2ac;
}

.mcp-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.7rem;
    color: #6e84a3;
    background: none;
    padding: 0;
    word-break: break-all;
}

input.mcp-mono {
    padding: 0.4375rem 0.75rem;
}

.mcp-status {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    white-space: nowrap;
}

.mcp-status-ready {
    background: #e6f6ee;
    color: #00854a;
}

.mcp-status-attention {
    background: #fbf0e3;
    color: #a5641d;
}

.mcp-status-blocked {
    background: #fdeaee;
    color: #e63757;
}

.mcp-status-off {
    background: #edf2f9;
    color: #6e84a3;
}

.mcp-chevron {
    flex-shrink: 0;
    font-size: 0.65rem;
    color: #d2ddec;
    transition: color 0.15s ease;
}

.mcp-tag {
    font-size: 0.6rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0.12rem 0.4rem;
    border-radius: 999px;
    white-space: nowrap;
}

.mcp-tag-shared {
    background: #e8f1fd;
    color: #2c7be5;
}

.mcp-tag-private {
    background: #edf2f9;
    color: #6e84a3;
}

/* Aqfer's own servers. Warmer than the "shared" blue so the rows nobody
   configured into existence are distinguishable at a glance from the ones an
   admin added for the whole client. */
.mcp-tag-builtin {
    background: #fdf0e3;
    color: #b45f06;
}

.mcp-tag-ready {
    background: #e6f6ee;
    color: #00854a;
}

/* The same amber as the status pill's "attention": a tag saying a sign-in has
   expired is the same news as the row's pill, so it should not be a new colour. */
.mcp-tag-attention {
    background: #fbf0e3;
    color: #a5641d;
}

.mcp-footnote {
    margin: 1rem 0 0;
    padding-top: 0.85rem;
    border-top: 1px solid #edf2f9;
    font-size: 0.75rem;
    color: #95aac9;
}

/* ── Empty state ──────────────────────────────────────────────────────────── */
.mcp-empty {
    text-align: center;
    padding: 2rem 1rem;
}

/* The list's empty external section: a bordered panel under its own heading,
   so the prompt reads as one more section rather than the whole card's state. */
.mcp-empty-boxed {
    padding: 1.75rem 1.25rem;
    border: 1px dashed #dbe4ef;
    border-radius: 0.5rem;
    background: #f9fbfd;
}

.mcp-empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    margin-bottom: 0.75rem;
    border-radius: 0.6rem;
    background: #f1f4f9;
    color: #b1c2d9;
}

.mcp-empty-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #12263f;
}

.mcp-empty .settings-help {
    max-width: 28rem;
    margin-inline: auto;
}

/* ── Detail ───────────────────────────────────────────────────────────────── */
.mcp-back {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem 0.25rem 0.35rem;
    margin: 0 0 0.5rem -0.35rem;
    border: 0;
    border-radius: 0.3rem;
    background: none;
    color: #6e84a3;
    font-size: 0.78rem;
    font-weight: 600;
    transition: color 0.15s ease, background-color 0.15s ease;
}

.mcp-back:hover {
    color: #2c7be5;
}

.mcp-back:focus-visible {
    outline: 2px solid #2c7be5;
    outline-offset: 1px;
}

/* Framed so the configuration reads as one record rather than six floating
   captions, and matching the `.resolved-box` readout on the Agent tab. */
.mcp-facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
    gap: 0;
    margin: 0;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    background: #f9fbfd;
    overflow: hidden;
}

.mcp-facts>div {
    padding: 0.6rem 0.85rem;
    border-top: none;
    min-width: 0;
}

/* The first row of each column already has the frame above it. */
.mcp-facts>div:first-child {
    border-top: 0;
}

.mcp-facts dt {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #95aac9;
    margin-bottom: 0.2rem;
}

.mcp-facts dd {
    margin: 0;
    font-size: 0.8rem;
    color: #12263f;
    min-width: 0;
}

/* ── Test result ──────────────────────────────────────────────────────────── */
.mcp-test {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.7rem 0.85rem;
    border-radius: 0.5rem;
    font-size: 0.78rem;
}

.mcp-test-ok {
    background: #e6f6ee;
    color: #00854a;
}

.mcp-test-bad {
    background: #fdeaee;
    color: #b52b45;
}

.mcp-test-tools {
    margin-top: 0.25rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.7rem;
    opacity: 0.85;
    word-break: break-word;
}

/* ── Availability + credential ────────────────────────────────────────────── */
.mcp-toggle {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    background: #f9fbfd;
}

.mcp-toggle .form-check-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #12263f;
}

.mcp-help {
    font-size: 0.75rem;
    color: #95aac9;
}

/* ── State notes ──────────────────────────────────────────────────────────────
   A tinted box for a sentence about the current state, keyed by severity. Same
   pastel families as the status pills so a card's note and its tag read as the
   same signal; the icon column keeps a wrapped second line aligned under the
   first rather than under the glyph. */
.mcp-note {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    margin-bottom: 0.9rem;
    padding: 0.7rem 0.85rem;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    font-size: 0.78rem;
    line-height: 1.45;
}

.mcp-note i {
    flex: none;
    margin-top: 0.1rem;
    font-size: 0.85rem;
}

.mcp-note-success {
    background: #e6f6ee;
    border-color: #c3e8d6;
    color: #00703e;
}

.mcp-note-warning {
    background: #fdf3e3;
    border-color: #f3ddb9;
    color: #9c5c05;
}

.mcp-note-error {
    background: #fdeaee;
    border-color: #f6c7d1;
    color: #c22a45;
}

.mcp-field {
    margin-bottom: 1rem;
}

.mcp-error {
    margin: 0 0 0.75rem;
    font-size: 0.78rem;
    color: #e63757;
}

/* Same footer shape as the Agent and Context tabs: what the state is on the
   left, what you can do about it on the right. Declared here for the same
   reason the `settings-*` names are — Settings.vue's block is scoped. */
.card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.footer-status {
    font-size: 0.78rem;
}

/* ── Tool permissions ─────────────────────────────────────────────────────────
   The list is a settings form, not a catalog: every row's answer has to be
   readable in one pass down the right-hand edge, which is why the controls are
   a fixed-width segmented group rather than per-row dropdowns. */
.mcp-default-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0.85rem;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    background: #f9fbfd;
}

.mcp-eyebrow-note {
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
}

.mcp-tools {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid #edf2f9;
    border-radius: 0.375rem;
    overflow: hidden;
}

.mcp-tool {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0.85rem;
}

.mcp-tool + .mcp-tool {
    border-top: 1px solid #edf2f9;
}

.mcp-tool-text {
    min-width: 0;
}

.mcp-tool-name {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    font-size: 0.85rem;
    font-weight: 600;
    color: #12263f;
}

.mcp-tool-desc {
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: #6e84a3;
    /* Two lines is enough to tell tools apart; some servers ship paragraphs. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.mcp-tool-meta {
    margin-top: 0.25rem;
    font-size: 0.7rem;
    color: #95aac9;
}

.mcp-tag-danger {
    background: #fdeaed;
    color: #c9314a;
}

/* Same amber as the status pill's "attention", one severity step below
   danger: the tool writes but doesn't destroy anything. */
.mcp-tag-warning {
    background: #fbf0e3;
    color: #a5641d;
}

/* Segmented control. One control, three states, and the selected one carries
   its own meaning in colour — green/amber/red match the rails used everywhere
   else on this page, so "what will happen" reads the same in both places. */
.mcp-modes {
    display: inline-flex;
    flex-shrink: 0;
    border: 1px solid #d2ddec;
    border-radius: 0.375rem;
    overflow: hidden;
    background: #fff;
}

.mcp-mode {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0.6rem;
    border: 0;
    background: transparent;
    font-size: 0.72rem;
    font-weight: 600;
    color: #95aac9;
    white-space: nowrap;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.mcp-mode + .mcp-mode {
    border-left: 1px solid #edf2f9;
}

.mcp-mode:hover:not(:disabled):not(.active) {
    background: #f9fbfd;
    color: #6e84a3;
}

.mcp-mode:disabled {
    cursor: default;
}

/* A disabled control still has to say what the setting *is* — read-only is not
   the same as unset — so only the unselected buttons fade. */
.mcp-mode:disabled:not(.active) {
    opacity: 0.5;
}

.mcp-mode.active.mcp-mode-allow {
    background: #e6f6ee;
    color: #00854a;
}

.mcp-mode.active.mcp-mode-ask {
    background: #fdf3e7;
    color: #b56a1d;
}

.mcp-mode.active.mcp-mode-deny {
    background: #fdeaed;
    color: #c9314a;
}

@media (max-width: 575.98px) {

    /* Stacked, the control keeps its full width rather than being squeezed
       into an unreadable strip beside the name. */
    .mcp-tool,
    .mcp-default-row {
        flex-direction: column;
        align-items: stretch;
    }
}

@media (prefers-reduced-motion: reduce) {

    .mcp-row,
    .mcp-back,
    .mcp-chevron,
    .mcp-mode {
        transition: none;
    }
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` is set on an ancestor outside this component, so a
   scoped selector could never match it. Mirrors the dark block in Settings.vue,
   which owns the shared `settings-*` names on the page itself. */
body.dark-mode {

    .settings-subtitle,
    .settings-help,
    .settings-loading,
    .mcp-eyebrow,
    .mcp-tally,
    .mcp-meta,
    .mcp-footnote,
    .mcp-help,
    .mcp-facts dt {
        color: $dark-text-muted !important;
    }

    /* ── Tally ─────────────────────────────────────────────────────────────── */
    .mcp-tally {
        background: $dark-body-bg !important;
        border-bottom-color: $dark-border-color !important;
    }

    .mcp-tally-item strong {
        color: $dark-text-primary !important;
    }

    .mcp-dot-off {
        background: #4a5361 !important;
    }

    /* ── List rows ─────────────────────────────────────────────────────────── */
    .mcp-row {
        background: $dark-input-bg !important;
        border-color: $dark-border-color !important;
    }

    .mcp-row:hover {
        border-color: lighten($dark-border-color, 10%) !important;
        box-shadow: none !important;
    }

    .mcp-row:active {
        border-color: $dark-accent !important;
    }

    .mcp-name,
    .mcp-facts dd,
    .mcp-empty-title,
    .mcp-toggle .form-check-label {
        color: $dark-text-primary !important;
    }

    .mcp-icon {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .mcp-icon-off {
        color: #5c6675 !important;
    }

    .mcp-mono {
        color: $dark-text-secondary !important;
    }

    .mcp-chevron {
        color: #4a5361 !important;
    }

    .mcp-row:hover .mcp-chevron {
        color: $dark-text-secondary !important;
    }

    .mcp-rail-off {
        background: #4a5361 !important;
    }

    .mcp-footnote {
        border-top-color: $dark-border-color !important;
    }

    /* ── Status pills and tags ─────────────────────────────────────────────────
       Translucent tints rather than the light theme's solid pastels: on a near
       black card those read as bright rectangles, and the tint keeps the pill
       reading as a label on the surface it sits on. */
    .mcp-status-ready,
    .mcp-tag-ready {
        background: rgba(0, 168, 84, 0.16) !important;
        color: #4fd18a !important;
    }

    /* State notes: same translucent-tint treatment as the pills, with a border
       a shade stronger than the fill so the box still has an edge on a dark
       card where the tint alone nearly disappears. */
    .mcp-note-success {
        background: rgba(0, 168, 84, 0.14) !important;
        border-color: rgba(0, 168, 84, 0.32) !important;
        color: #4fd18a !important;
    }

    .mcp-note-warning {
        background: rgba(217, 130, 43, 0.14) !important;
        border-color: rgba(217, 130, 43, 0.34) !important;
        color: #e0a458 !important;
    }

    .mcp-note-error {
        background: rgba(230, 55, 87, 0.14) !important;
        border-color: rgba(230, 55, 87, 0.32) !important;
        color: #f08a9c !important;
    }

    .mcp-status-attention,
    .mcp-tag-attention {
        background: rgba(217, 130, 43, 0.18) !important;
        color: #e0a458 !important;
    }

    .mcp-status-blocked {
        background: rgba(230, 55, 87, 0.16) !important;
        color: #f08a9c !important;
    }

    .mcp-status-off,
    .mcp-tag-private {
        background: $dark-elevated-bg !important;
        color: $dark-text-secondary !important;
    }

    .mcp-tag-shared {
        background: $dark-selected-bg !important;
        color: #7ab0ff !important;
    }

    .mcp-tag-builtin {
        background: rgba(217, 130, 43, 0.18) !important;
        color: #e0a458 !important;
    }

    /* ── Empty state ───────────────────────────────────────────────────────── */
    .mcp-empty-boxed {
        border-color: $dark-border-color !important;
        background: $dark-body-bg !important;
    }

    .mcp-empty-icon {
        background: $dark-elevated-bg !important;
        color: #5c6675 !important;
    }

    /* ── Detail ────────────────────────────────────────────────────────────── */
    .mcp-back {
        color: $dark-text-secondary !important;
    }

    .mcp-back:hover {
        color: #7ab0ff !important;
    }

    .mcp-facts {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .mcp-facts>div {
        border-top-color: $dark-border-color !important;
    }

    .mcp-toggle {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    /* ── Verdict ───────────────────────────────────────────────────────────── */
    .mcp-verdict-ready {
        background: rgba(0, 168, 84, 0.12) !important;
        color: #5fd3a3 !important;
    }

    .mcp-verdict-attention {
        background: rgba(217, 130, 43, 0.14) !important;
        color: #e0a458 !important;
    }

    .mcp-verdict-blocked {
        background: rgba(230, 55, 87, 0.13) !important;
        color: #f08a9c !important;
    }

    .mcp-verdict-off {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
        color: $dark-text-secondary !important;
    }

    /* ── Tool permissions ──────────────────────────────────────────────────────
       The list is the settings-side twin of the in-chat permission card, so the
       allow / ask / deny shades here are the same ones McpToolPermissionPrompt
       uses on dark. */
    .mcp-default-row {
        background: $dark-body-bg !important;
        border-color: $dark-border-color !important;
    }

    .mcp-tools {
        border-color: $dark-border-color !important;
    }

    .mcp-tool+.mcp-tool {
        border-top-color: $dark-border-color !important;
    }

    .mcp-tool-name {
        color: $dark-text-primary !important;
    }

    .mcp-tool-desc {
        color: $dark-text-secondary !important;
    }

    .mcp-tool-meta {
        color: $dark-text-muted !important;
    }

    .mcp-tag-danger {
        background: rgba(230, 55, 87, 0.16) !important;
        color: #f08a9c !important;
    }

    .mcp-tag-warning {
        background: rgba(217, 130, 43, 0.18) !important;
        color: #e0a458 !important;
    }

    /* Segmented control. The light theme's `background: #fff` is a white block
       on a near-black card, so the whole control has to be re-grounded — and the
       selected state's pastel fills lose their meaning at this brightness,
       hence translucent tints over a light text colour instead. */
    .mcp-modes {
        background: $dark-input-bg !important;
        border-color: $dark-border-color !important;
    }

    .mcp-mode {
        color: $dark-text-secondary !important;
    }

    .mcp-mode+.mcp-mode {
        border-left-color: $dark-border-color !important;
    }

    .mcp-mode:hover:not(:disabled):not(.active) {
        background: $dark-hover-bg !important;
        color: $dark-text-primary !important;
    }

    .mcp-mode.active.mcp-mode-allow {
        background: rgba(0, 168, 84, 0.18) !important;
        color: #4fd18a !important;
    }

    .mcp-mode.active.mcp-mode-ask {
        background: rgba(217, 130, 43, 0.2) !important;
        color: #e0a458 !important;
    }

    .mcp-mode.active.mcp-mode-deny {
        background: rgba(230, 55, 87, 0.18) !important;
        color: #f08a9c !important;
    }

    /* ── Test result ───────────────────────────────────────────────────────── */
    .mcp-test-ok {
        background: rgba(0, 217, 126, 0.12) !important;
        color: #5fd3a3 !important;
    }

    .mcp-test-bad {
        background: rgba(230, 55, 87, 0.14) !important;
        color: #f08a9c !important;
    }

    .mcp-error {
        color: #f08a9c !important;
    }

    .mcp-sep {
        color: #d2ddec;
    }
}
</style>
